import {
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { ImportFileCsv } from 'components/domain/csv/ImportFileCsv'
import { Button, CloseIcon } from 'components/facets'
import { useState } from 'react'
import Dropzone from 'react-dropzone'
import {
  checkCardmarketStockForV2Migration,
  Game,
  gameFromStockFileName,
  gameMetaData,
  GAME_IDS,
  InventoryArticle,
  V2MigrationReport,
} from 'shared'
import { V2MigrationReportView } from './V2MigrationCheckDialog'

const CSV_GAMES = gameMetaData.filter((game) => GAME_IDS.includes(game.idGame))

interface QueuedFile {
  file: File
  game?: Game
  report?: V2MigrationReport
}

// For a seller who is not a PowerTools user yet, so there is no Cardmarket account to export from:
// their stock comes as CSV files (an onboarding backup, a Cardmarket export, their own files), each
// going through the same import as the listing, comment parsing included. The files are imported
// one after the other, each through its own column preview.
export const V2MigrationCsvCheckDialog = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}): JSX.Element => {
  const [files, setFiles] = useState<QueuedFile[]>([])
  // The file whose column preview is open, if any.
  const [importing, setImporting] = useState<number>()

  const updateFile = (index: number, change: Partial<QueuedFile>) =>
    setFiles((current) =>
      current.map((entry, i) => (i === index ? { ...entry, ...change } : entry))
    )

  const handleDrop = (dropped: File[]) =>
    setFiles((current) => [
      ...current,
      ...dropped.map((file) => ({ file, game: gameFromStockFileName(file.name) })),
    ])

  const nextToImport = (after: number) =>
    files.findIndex((entry, index) => index > after && !entry.report)

  // "Try again" in the preview imports the same file a second time: its report is replaced, not
  // added.
  const handleImportedArticles = (articles: InventoryArticle[]) => {
    if (importing === undefined) return
    const { game } = files[importing]
    updateFile(importing, { report: checkCardmarketStockForV2Migration(game!.idGame, articles) })
  }

  // The preview's own "Import" button: the file is done, the next one opens.
  const handlePreviewDone = () => {
    if (importing === undefined) return
    const next = nextToImport(importing)
    setImporting(next === -1 ? undefined : next)
  }

  const toImport = files.filter((entry) => !entry.report)
  const reports = files
    .filter((entry) => entry.report)
    .map((entry) => ({ game: entry.game!.name, report: entry.report! }))

  return (
    <ClosableDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <div className="m-4">
        <DialogTitle disableTypography>
          <h3>Check migration to v2 from a CSV</h3>
        </DialogTitle>
        <DialogContent>
          <Dropzone onDrop={handleDrop} multiple>
            {({ getRootProps, getInputProps }) => (
              //@ts-ignore
              <div {...getRootProps()} className="border border-2 rounded p-4 mb-3 text-center">
                <input {...getInputProps()} />
                <p className="m-0">Drop the CSV files here, or click to choose them.</p>
              </div>
            )}
          </Dropzone>

          {files.length > 0 && (
            <Table size="small" className="mb-3">
              <TableBody>
                {files.map((entry, index) => (
                  <TableRow key={`${entry.file.name}-${index}`}>
                    <TableCell>{entry.file.name}</TableCell>
                    <TableCell>
                      <TextField
                        select
                        label="Game"
                        value={entry.game?.idGame ?? ''}
                        disabled={!!entry.report}
                        error={!entry.game}
                        helperText={entry.game ? undefined : 'Not recognized: choose it'}
                        onChange={(event) =>
                          updateFile(index, {
                            game: CSV_GAMES.find(
                              ({ idGame }) => idGame === Number(event.target.value)
                            ),
                          })
                        }
                      >
                        {CSV_GAMES.map(({ idGame, name }) => (
                          <MenuItem key={idGame} value={idGame}>
                            {name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>{entry.report ? 'Checked' : 'To check'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                      >
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="d-flex justify-content-end gap-2 mb-3">
            {files.length > 0 && (
              <Button variant="outlined" onClick={() => setFiles([])}>
                Clear
              </Button>
            )}
            <Button
              variant="contained"
              disabled={toImport.length === 0 || toImport.some((entry) => !entry.game)}
              onClick={() => setImporting(nextToImport(-1))}
            >
              Check files
            </Button>
          </div>

          {reports.length > 0 && <V2MigrationReportView reports={reports} />}
        </DialogContent>
      </div>

      {importing !== undefined && (
        <ClosableDialog maxWidth="xl" fullWidth open onClose={() => setImporting(undefined)}>
          <div className="m-4">
            <h3>{files[importing].file.name}</h3>
            <ImportFileCsv
              // A new key per file, so each one starts from its own preview.
              key={importing}
              initialFile={files[importing].file}
              importedCsvGame={files[importing].game}
              handleResult={handleImportedArticles}
              closeExternalDialog={handlePreviewDone}
            />
          </div>
        </ClosableDialog>
      )}
    </ClosableDialog>
  )
}
